import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Tab,
  Tabs,
  Toolbar,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  Money,
  Paid,
  People,
  PointOfSale,
  PriceCheck,
  Savings as SavingsIcon,
  Today,
} from "@mui/icons-material";
import axios from "axios";
import { ResponsiveContainer, PieChart, Pie, Cell, Sector } from "recharts";
import { buildStyles, CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";

import Sidebar from "../sidebar/sidebar";
import {
  companylogo,
  card1,
  card2,
  card3,
  card4,
  card5,
  card6,
} from "../../assets/images";
import FilterBtn from "./FilterBtn";
import { getSecureItem } from "../../utilsFunctions";
import { BaseUrl } from "../../Environment";

// ─── Constants ────────────────────────────────────────────────────────────────

// Defined outside component — never recreated on re-render
const theme = createTheme({
  typography: { fontFamily: "'Lato'" },
  palette: { background: { default: "#f0f2f8" } },
});

const TABS = [
  { value: "loans", label: "Loans" },
  { value: "savings", label: "Savings" },
];

const DEFAULT_FILTERS = {
  dates: [],
  branches: [],
  agents: [],
  dateFilterType: ["today"],
};

const DATE_FILTER_LABEL = {
  today: "Today's",
  week: "Week's",
  month: "Month's",
};

const CARD_OVERLAY_STYLE = {
  position: "relative",
  backgroundSize: "cover",
  borderRadius: 3,
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: "rgba(255,255,255,0.8)",
  },
};

const COLLECTION_CARD_COLORS = ["#e3c848", "#5ac796", "#b57dff", "#ff6d91"];

const MASKED = "*****";

const STATUS_ITEMS = [
  {
    selectedBg: "#dffaea",
    color: "#00d95e",
    key: "TotalCollection",
    labelSuffix: "Collections",
  },
  {
    selectedBg: "#e8ebfd",
    color: "#4d66f0",
    key: "TotalCollectionDue",
    labelSuffix: "Total Dues",
  },
  {
    selectedBg: "#f7e0e0",
    color: "#c10707",
    key: "overdueAmount",
    label: "Unmarked Dues",
  },
];

// Same three slots, savings keys. TotalSavingsCollection / TotalSavingsDue are
// period figures (they follow the date filter); overdueAmount is always
// "unmarked dues scheduled up to today", exactly like the loans tab.
const SAVINGS_STATUS_ITEMS = [
  {
    selectedBg: "#dffaea",
    color: "#00d95e",
    key: "TotalSavingsCollection",
    labelSuffix: "Collections",
  },
  {
    selectedBg: "#e8ebfd",
    color: "#4d66f0",
    key: "TotalSavingsDue",
    labelSuffix: "Total Dues",
  },
  {
    selectedBg: "#f7e0e0",
    color: "#c10707",
    key: "overdueAmount",
    label: "Unmarked Dues",
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const renderActiveShape = (props) => {
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    payload,
    percent,
  } = props;

  return (
    <g>
      <text
        x={cx}
        y={cy}
        dy={8}
        textAnchor="middle"
        fill={payload.fontColor}
        style={{ fontWeight: 600 }}
      >
        {Math.round(percent * 100)}%
      </text>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius - 6}
        outerRadius={outerRadius + 6}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={payload.fontColor}
      />
    </g>
  );
};

// Defined outside — receives only primitives, no unnecessary re-renders
const NO_DATA_PIE = [
  {
    name: "No Data",
    amount: 1,
    value: "default",
    fontColor: "#ccc",
    pieBackground: "#eee",
  },
];

function DonutChart({ collections, totalDues, unmarkedDues, activeIndex }) {
  const data = useMemo(
    () => [
      {
        name: "Collected",
        amount: collections,
        value: "collected",
        fontColor: "#00d95e",
        pieBackground: "#66e89e",
      },
      {
        name: "Due",
        amount: totalDues,
        value: "due",
        fontColor: "#4d66f0",
        pieBackground: "#94a3f6",
      },
      {
        name: "Overdue",
        amount: unmarkedDues,
        value: "overdue",
        fontColor: "#c10707",
        pieBackground: "#db8381",
      },
    ],
    [collections, totalDues, unmarkedDues],
  );

  const finalData = useMemo(
    () =>
      data.reduce((sum, item) => sum + item.amount, 0) === 0
        ? NO_DATA_PIE
        : data,
    [data],
  );

  return (
    <ResponsiveContainer width="100%" height={267}>
      <PieChart>
        <Pie
          activeIndex={activeIndex}
          activeShape={renderActiveShape}
          data={finalData}
          cx="50%"
          cy="50%"
          innerRadius={70}
          outerRadius={90}
          dataKey="amount"
        >
          {finalData.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.pieBackground}
              style={{ stroke: "none" }}
            />
          ))}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  );
}

function CollectionCard({ card, index }) {
  const currency = getSecureItem("country")?.symbol;
  const color = COLLECTION_CARD_COLORS[index % COLLECTION_CARD_COLORS.length];

  const collected =
    parseFloat(String(card.amount).replace(/[^0-9.]/g, "")) || 0;
  const total = parseFloat(String(card.total).replace(/[^0-9.]/g, "")) || 0;
  const percentage = total > 0 ? Math.round((collected / total) * 100) : 0;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        backgroundColor: color,
        boxShadow: `0 4px 20px ${color}CC`,
        color: "#fff",
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Typography
          variant="body2"
          sx={{ opacity: 0.9, mb: 4, color: "#fff", fontWeight: 600 }}
        >
          {card.label}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ width: 56, height: 56, flexShrink: 0 }}>
            <CircularProgressbar
              value={percentage}
              text={`${percentage}%`}
              styles={buildStyles({
                textSize: "28px",
                textColor: "rgba(255,255,255,0.85)",
                pathColor: "rgba(255,255,255,0.9)",
                trailColor: "rgba(255,255,255,0.25)",
              })}
            />
          </Box>
          <Box>
            <Typography
              variant="h5"
              sx={{
                color: "#fff",
                fontSize: 28,
                fontWeight: 800,
                lineHeight: 1.2,
              }}
            >
              {currency} {card.amount}
            </Typography>
            <Typography variant="caption" sx={{ color: "#fff", fontSize: 14 }}>
              Out of {currency} {card.total}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function PortfolioCard({ card, index }) {
  const currency = getSecureItem("country")?.symbol;
  return (
    <Card
      elevation={0}
      sx={{ ...CARD_OVERLAY_STYLE, backgroundImage: `url(${card.bgImage})` }}
    >
      <CardContent sx={{ p: 2, position: "relative", zIndex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
          {card.icon}
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {card.label}
          </Typography>
        </Box>
        <Typography
          variant="h5"
          fontWeight={800}
          color="text.primary"
          sx={{ mb: 0.5 }}
        >
          {index == 3 || index == 4 || index == 5
            ? `${currency} ${card.value}`
            : card.value}
        </Typography>
      </CardContent>
    </Card>
  );
}

function SectionCard({ title, children }) {
  return (
    <Card elevation={1} sx={{ borderRadius: 4, bgcolor: "#fff", p: 1, boxShadow: "0px 3px 20px 4px rgba(36, 38, 52, 0.2)" }}>
      <CardContent>
        <Typography variant="h6" fontWeight={800} sx={{ mb: 2.5 }}>
          {title}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );
}

// Status card, shared by both tabs. activeIndex lives here because nothing
// outside this card ever read it.
function StatusPanel({
  statusItems,
  collections,
  totalDues,
  unmarkedDues,
  onFilterChange,
}) {
  const currency = getSecureItem("country")?.symbol;
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <Card elevation={1} sx={{ borderRadius: 4, bgcolor: "#fff", boxShadow: "0px 3px 20px 4px rgba(36, 38, 52, 0.2)" }}>
      <CardContent sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            // mb: 3,
          }}
        >
          <Typography variant="h6" fontWeight={800}>
            Status
          </Typography>
          <FilterBtn onFilterChange={onFilterChange} />
        </Box>

        <DonutChart
          collections={collections}
          totalDues={totalDues}
          unmarkedDues={unmarkedDues}
          activeIndex={activeIndex}
        />

        <Grid container>
          {statusItems.map((item, index) => {
            const isSelected = activeIndex === index;
            return (
              <Grid item xs={12} md={4} key={index}>
                <Box
                  onClick={() => setActiveIndex(index)}
                  sx={{
                    bgcolor: isSelected ? item.selectedBg : "#fff",
                    p: 1.5,
                    textAlign: "center",
                    cursor: "pointer",
                    borderRadius: 1,
                    transition: "all 0.2s ease",
                  }}
                >
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color={item.color}
                    fontSize={18}
                  >
                    {currency} {item.value}
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    display="block"
                    color={item.color}
                    fontSize={11}
                  >
                    {item.label}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </Card>
  );
}

// ─── Loans tab ────────────────────────────────────────────────────────────────
// Unchanged from the previous single-purpose NewDashboard: same payload, same
// two endpoints, same cards, same masking. Only the outer chrome moved out.

function LoansDashboard() {
  const loggedinUser = getSecureItem("loggedinUser");
  const userPermissions = getSecureItem("UsersAccess");

  const [dashData, setDashData] = useState(null);
  const [acOverviewData, setAcOverviewData] = useState(null);
  const [filtersData, setFiltersData] = useState(DEFAULT_FILTERS);

  const handleFilterChange = useCallback((filters) => {
    setFiltersData(filters);
  }, []);

  // ─── Data fetching ──────────────────────────────────────────────────────────

  const getDashData = useCallback(async () => {
    try {
      const token = getSecureItem("token");
      const { dates, branches, agents, dateFilterType } = filtersData;

      const payload = {
        companyId: loggedinUser?.companyId,
        collectorId:
          loggedinUser?.userType === "Collection Agent"
            ? loggedinUser?.userId
            : (agents[0]?.value || loggedinUser?.userId),
        fromDate: dates[0]?.startDate ? new Date(dates[0].startDate) : null,
        toDate: dates[0]?.endDate ? new Date(dates[0].endDate) : null,
        branchId: branches[0]?.value ?? loggedinUser?.branchId ?? null,
        areaLine: null,
        dateFilterType: dateFilterType[0],
      };

      const { data } = await axios.post(
        `${BaseUrl}/loanCollection/getCollectionDashboardMobileNew`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDashData(data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  }, [filtersData]);

  const getAccountOverview = useCallback(async () => {
    try {
      const token = getSecureItem("token");
      const loggedinUser = getSecureItem("loggedinUser");
      const { branches } = filtersData;

      const { data } = await axios.get(
        `${BaseUrl}/loanCollection/accountOverview?companyId=${loggedinUser?.companyId}&branchId=${loggedinUser?.userType == "superAdmin" ? (branches[0]?.value || "") : loggedinUser?.branchId ?? ''}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAcOverviewData(data);
    } catch (error) {
      console.error("Error fetching account overview data:", error);
    }
  }, [filtersData]); // re-runs when the branch filter changes

  useEffect(() => {
    getDashData();
  }, [getDashData]);
  useEffect(() => {
    getAccountOverview();
  }, [getAccountOverview]);

  // ─── Derived data ───────────────────────────────────────────────────────────

  const dateLabel =
    DATE_FILTER_LABEL[filtersData.dateFilterType[0]] ?? "Custom Date";

  const canViewAccounting = loggedinUser?.userType === "superAdmin" || userPermissions?.accounting;
  const collectionCards = useMemo(
    () => [
      {
        label: "Loan Disbursed",
        amount: canViewAccounting ? dashData?.totalLoanDisbursedOpen ?? 0 : MASKED,
        total: canViewAccounting ? dashData?.loanDisbursed ?? 0 : MASKED,
      },
      {
        label: "Repayable Amount",
        amount: canViewAccounting ? dashData?.totalRepayableOpen ?? 0 : MASKED,
        total: canViewAccounting ? dashData?.totalRepayable ?? 0 : MASKED,
      },
      {
        label: "Received Amount",
        amount: canViewAccounting ? dashData?.receivedAmount ?? 0 : MASKED,
        total: canViewAccounting ? dashData?.totalRepayable ?? 0 : MASKED,
      },
      {
        label: "Outstanding Balance",
        amount: canViewAccounting ? dashData?.totalOutstanding ?? 0 : MASKED,
        total: canViewAccounting ? dashData?.totalRepayable ?? 0 : MASKED,
      },
    ],
    [dashData, canViewAccounting],
  );

  const portfolioCards = useMemo(
    () => [
      {
        label: "Active Loans",
        value: acOverviewData?.ActiveLoans ?? 0,
        icon: <PriceCheck sx={{ color: "#4f8ef7", fontSize: 28 }} />,
        bgImage: card2,
      },
      {
        label: "Active Customers",
        value: acOverviewData?.ActiveCustomers ?? 0,
        icon: <People sx={{ color: "#7c3aed", fontSize: 28 }} />,
        bgImage: card3,
      },
      {
        label: "Overdue Loans",
        value: acOverviewData?.OverDueLoans ?? 0,
        icon: <Today sx={{ color: "#f3784fff", fontSize: 28 }} />,
        bgImage: card4,
      },
      {
        label: "Total Expenses",
        value: acOverviewData?.ExpensesTotal ?? 0,
        icon: <PointOfSale sx={{ color: "#f3b64dff", fontSize: 28 }} />,
        bgImage: card2,
      },
      {
        label: "Total Principal Amount",
        value: acOverviewData?.TotalPrincipal ?? 0,
        icon: <Paid sx={{ color: "#4a63f2ff", fontSize: 28 }} />,
        bgImage: card3,
      },
      {
        label: "Total Interest Amount",
        value:
          (acOverviewData?.InterestManual ?? 0) +
          (acOverviewData?.InterestInstall ?? 0) +
          (acOverviewData?.InterestBalloon ?? 0),
        icon: <Money sx={{ color: "#f94343ff", fontSize: 28 }} />,
        bgImage: card4,
      },
    ],
    [acOverviewData],
  );

  // STATUS_ITEMS is static; only values come from dashData
  const statusItems = useMemo(
    () =>
      STATUS_ITEMS.map((item) => ({
        ...item,
        value: dashData?.[item.key] ?? 0,
        label: item.label ?? `${dateLabel} ${item.labelSuffix}`,
      })),
    [dashData, dateLabel],
  );

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Collection Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {collectionCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <CollectionCard card={card} index={index} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} mt={2}>
        {/* Portfolio Overview */}
        <Grid item xs={12} md={8}>
          <SectionCard title="Account Overview">
            <Grid container spacing={4}>
              {portfolioCards.map((card, index) => (
                <Grid item xs={12} sm={6} md={4} mt={3} key={card.label}>
                  <PortfolioCard card={card} index={index} />
                </Grid>
              ))}
            </Grid>
          </SectionCard>
        </Grid>

        {/* Status Panel */}
        <Grid item xs={12} md={4}>
          <StatusPanel
            statusItems={statusItems}
            collections={dashData?.TotalCollection ?? 0}
            totalDues={dashData?.TotalCollectionDue ?? 0}
            unmarkedDues={dashData?.overdueAmount ?? 0}
            onFilterChange={handleFilterChange}
          />
        </Grid>
      </Grid>
    </>
  );
}

// ─── Savings tab ──────────────────────────────────────────────────────────────
// Same shape as the loans tab, savings endpoints. Nothing is shared as state,
// so switching tabs starts the other side from DEFAULT_FILTERS.

function SavingsDashboard() {
  const loggedinUser = getSecureItem("loggedinUser");
  const userPermissions = getSecureItem("UsersAccess");

  const [dashData, setDashData] = useState(null);
  const [acOverviewData, setAcOverviewData] = useState(null);
  const [filtersData, setFiltersData] = useState(DEFAULT_FILTERS);

  const handleFilterChange = useCallback((filters) => {
    setFiltersData(filters);
  }, []);

  // ─── Data fetching ──────────────────────────────────────────────────────────

  const getDashData = useCallback(async () => {
    try {
      const token = getSecureItem("token");
      const { dates, branches, agents, dateFilterType } = filtersData;

      const payload = {
        companyId: loggedinUser?.companyId,
        // NOTE: deliberately NOT the loans line
        //   (agents[0]?.value || loggedinUser?.userId)
        // which narrows an admin's view to accounts assigned to the admin
        // themselves whenever no agent is picked. Here an unset agent filter
        // means "whole book". Swap in the loans expression if you want the two
        // tabs to behave identically.
        collectorId:
          loggedinUser?.userType === "Collection Agent"
            ? loggedinUser?.userId
            : (agents[0]?.value || null),
        fromDate: dates[0]?.startDate ? new Date(dates[0].startDate) : null,
        toDate: dates[0]?.endDate ? new Date(dates[0].endDate) : null,
        branchId: branches[0]?.value ?? loggedinUser?.branchId ?? null,
        areaLine: null,
        dateFilterType: dateFilterType[0],
      };

      const { data } = await axios.post(
        `${BaseUrl}/savings/getSavingsDashboardMobile`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDashData(data);
    } catch (error) {
      console.error("Error fetching savings dashboard data:", error);
    }
  }, [filtersData]);

  const getAccountOverview = useCallback(async () => {
    try {
      const token = getSecureItem("token");
      const { branches, agents } = filtersData;

      const params = {
        companyId: loggedinUser?.companyId,
        branchId:
          loggedinUser?.userType == "superAdmin"
            ? (branches[0]?.value || "")
            : loggedinUser?.branchId ?? "",
        // agents can only ever see their own book
        collectorId:
          loggedinUser?.userType === "Collection Agent"
            ? loggedinUser?.userId
            : (agents[0]?.value || ""),
      };

      const { data } = await axios.get(
        `${BaseUrl}/savings/getSavingsAccountOverview`,
        { params, headers: { Authorization: `Bearer ${token}` } },
      );
      setAcOverviewData(data);
    } catch (error) {
      console.error("Error fetching savings account overview data:", error);
    }
  }, [filtersData]);

  useEffect(() => {
    getDashData();
  }, [getDashData]);
  useEffect(() => {
    getAccountOverview();
  }, [getAccountOverview]);

  // ─── Derived data ───────────────────────────────────────────────────────────

  const dateLabel =
    DATE_FILTER_LABEL[filtersData.dateFilterType[0]] ?? "Custom Date";

  const canViewAccounting = loggedinUser?.userType === "superAdmin" || userPermissions?.accounting;

  // Card 1-3 are book positions measured against the agreed target; card 4 is
  // the period figure the date filter drives. "Savings Balance" is the
  // customer's accumulated deposit (it grows as they save) — NOT a debt, which
  // is why "Outstanding To Collect" is shown separately as target - collected.
  const collectionCards = useMemo(
    () => [
      {
        label: "Savings Collected",
        amount: canViewAccounting ? dashData?.receivedAmount ?? 0 : MASKED,
        total: canViewAccounting ? dashData?.totalTarget ?? 0 : MASKED,
      },
      {
        label: "Interest Collected",
        amount: canViewAccounting ? dashData?.receivedInterest ?? 0 : MASKED,
        total: canViewAccounting ? dashData?.totalInterest ?? 0 : MASKED,
      },
      {
        label: "Outstanding To Collect",
        amount: canViewAccounting ? dashData?.totalOutstanding ?? 0 : MASKED,
        total: canViewAccounting ? dashData?.totalTarget ?? 0 : MASKED,
      },
      {
        label: `${dateLabel} Collections`,
        amount: canViewAccounting
          ? dashData?.TotalSavingsCollection ?? 0
          : MASKED,
        total: canViewAccounting ? dashData?.TotalSavingsDue ?? 0 : MASKED,
      },
    ],
    [dashData, dateLabel, canViewAccounting],
  );

  // Slots 0-2 must stay counts and 3-5 money: PortfolioCard prefixes the
  // currency symbol on index 3, 4 and 5 only.
  const portfolioCards = useMemo(
    () => [
      {
        label: "Active Accounts",
        value: acOverviewData?.ActiveSavings ?? 0,
        icon: <PriceCheck sx={{ color: "#4f8ef7", fontSize: 28 }} />,
        bgImage: card2,
      },
      {
        label: "Active Customers",
        value: acOverviewData?.ActiveCustomers ?? 0,
        icon: <People sx={{ color: "#7c3aed", fontSize: 28 }} />,
        bgImage: card3,
      },
      {
        label: "Overdue Savings",
        value: acOverviewData?.OverDueSavings ?? 0,
        icon: <Today sx={{ color: "#f3784fff", fontSize: 28 }} />,
        bgImage: card4,
      },
      {
        label: "Savings Balance",
        value: acOverviewData?.SavingsBalance ?? 0,
        icon: <SavingsIcon sx={{ color: "#12b981", fontSize: 28 }} />,
        bgImage: card1,
      },
      {
        label: "Interest Accrued",
        value: acOverviewData?.InterestAccrued ?? 0,
        icon: <Money sx={{ color: "#f94343ff", fontSize: 28 }} />,
        bgImage: card5,
      },
      {
        label: "Maturity Value",
        value: acOverviewData?.MaturityValue ?? 0,
        icon: <Paid sx={{ color: "#4a63f2ff", fontSize: 28 }} />,
        bgImage: card6,
      },
    ],
    [acOverviewData],
  );

  const statusItems = useMemo(
    () =>
      SAVINGS_STATUS_ITEMS.map((item) => ({
        ...item,
        value: dashData?.[item.key] ?? 0,
        label: item.label ?? `${dateLabel} ${item.labelSuffix}`,
      })),
    [dashData, dateLabel],
  );

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Savings Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {collectionCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <CollectionCard card={card} index={index} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} mt={2}>
        {/* Savings Overview */}
        <Grid item xs={12} md={8}>
          <SectionCard title="Savings Overview">
            <Grid container spacing={4}>
              {portfolioCards.map((card, index) => (
                <Grid item xs={12} sm={6} md={4} mt={3} key={card.label}>
                  <PortfolioCard card={card} index={index} />
                </Grid>
              ))}
            </Grid>
          </SectionCard>
        </Grid>

        {/* Status Panel */}
        <Grid item xs={12} md={4}>
          <StatusPanel
            statusItems={statusItems}
            collections={dashData?.TotalSavingsCollection ?? 0}
            totalDues={dashData?.TotalSavingsDue ?? 0}
            unmarkedDues={dashData?.overdueAmount ?? 0}
            onFilterChange={handleFilterChange}
          />
        </Grid>
      </Grid>
    </>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function NewDashboard() {
  const loggedinUser = getSecureItem("loggedinUser");
  const userPermissions = getSecureItem("UsersAccess");

  // No viewSavings flag exists yet, so absence means "allowed". Add the flag to
  // UsersAccess and this starts gating the tab with no further changes here.
  const canViewSavings =
    loggedinUser?.userType === "superAdmin" ||
    (userPermissions?.viewSavings ?? true);

  const [tab, setTab] = useState("loans");
  const activeTab = canViewSavings ? tab : "loans";
  const visibleTabs = TABS.filter(
    (item) => item.value !== "savings" || canViewSavings,
  );

  return (
    <>
      <Sidebar />
      <div style={{ marginLeft: "73px", paddingInline: 30 }}>
        <Toolbar sx={{ backgroundColor: "white", gap: 2 }}>
          <img src={companylogo} alt="logo" />
          <Box sx={{ flexGrow: 1 }} />
          {visibleTabs.length > 1 && (
            <Tabs
              value={activeTab}
              onChange={(_, value) => setTab(value)}
              TabIndicatorProps={{ sx: { display: "none" } }}
              sx={{
                minHeight: 0,
                bgcolor: "#f0f2f8",
                borderRadius: "999px",
                p: 0.5,
                "& .MuiTabs-flexContainer": { gap: 0.5 },
              }}
            >
              {visibleTabs.map((item) => (
                <Tab
                  key={item.value}
                  value={item.value}
                  label={item.label}
                  disableRipple
                  sx={{
                    minHeight: 0,
                    minWidth: 110,
                    py: 1,
                    borderRadius: "999px",
                    fontFamily: "'Lato'",
                    fontWeight: 700,
                    fontSize: 14,
                    textTransform: "none",
                    color: "#6b7280",
                    "&.Mui-selected": {
                      color: "#4d66f0",
                      bgcolor: "#fff",
                      boxShadow: "0 2px 8px rgba(36,38,52,0.12)",
                    },
                  }}
                />
              ))}
            </Tabs>
          )}
        </Toolbar>

        <ThemeProvider theme={theme}>
          <Box sx={{ p: 3 }}>
            {/* only the active tab is mounted, so the other never fetches */}
            {activeTab === "savings" ? <SavingsDashboard /> : <LoansDashboard />}
          </Box>
        </ThemeProvider>
      </div>
    </>
  );
}
